import {
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
  createContext,
} from "react";
import { createUseStyles } from "react-jss";

import TextInput from "../inputs/TextInput";

export const TextContext = createContext(() => {});

export const useTextSelector = initial => {
  const [text, setText] = useState(initial);
  const context = useContext(TextContext);

  return [
    text,
    content => context(m => setText(m ?? initial), content),
    setText,
  ];
};

const useStyles = createUseStyles(theme => ({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    "pointer-events": props => (props.show ? "auto" : "none"),
    "z-index": 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    "background-color": "rgba(0, 0, 0, 0.7)",
    "z-index": -1,
  },
  loading: {
    color: "white",
  },
  container: {
    width: "90%",
    "max-width": 330,
    "background-color": "#313131",
    "padding-bottom": 15,
    "border-radius": 5,
    "box-sizing": "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  button: {
    color: "white",
    "border-radius": 5,
    padding: [8, 30],
    "font-size": 16,
    border: "none",
    "background-color": theme.colorRed,
    cursor: "pointer",
    float: "right",
    marginTop: 15,
  },
}));

const TextSelector = ({ show, onClose, onSubmit, name, value, label }) => {
  const [text, setText] = useState();
  const classes = useStyles({ show });
  const ref = useRef();

  useEffect(() => {
    setText(value);
    ref.current.focus();
  }, [value]);

  return (
    <div className={classes.background}>
      <div className={classes.dim} onClick={onClose} />
      <div className={classes.container}>
        <div className={classes.relative}>
          <h1>{name}</h1>
          <TextInput
            ref={ref}
            label={label}
            value={text}
            onChange={s => setText(s)}
            required
          />
          <button className={classes.button} onClick={() => onSubmit(text)}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const initialContent = { name: "", label: "", value: "" };

const TextSelectorProvider = ({ children }) => {
  const [showText, setShowText] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [textCallback, setTextCallback] = useState(() => () => {});

  const selectText = useCallback((callback, content = {}) => {
    setShowText(true);
    setContent({ ...initialContent, ...content });
    setTextCallback(() => (...args) => {
      setShowText(false);
      callback(...args);
    });
  }, []);

  return (
    <TextContext.Provider value={selectText}>
      <TextSelector
        show={showText}
        onSubmit={textCallback}
        onClose={() => setShowText(false)}
        {...content}
      />
      {children}
    </TextContext.Provider>
  );
};

export default TextSelectorProvider;
